// Bảng tra khung → tranh vẽ thật (Codex image_gen, CEO duyệt 17/10/2026).
//
// Khung tròn = kiểu dáng (người đeo tự chọn, CEO 6a·7a) × kim loại (theo món đang đeo).
// Ngọc gắn ở ổ đỉnh/đáy đổi màu theo NHÓM món — cùng một kim loại, bảy nhóm nhìn khác
// nhau bằng màu ngọc. Khung thẻ chữ nhật + biển bậc nghề + huy hiệu kệ chiến tích chỉ
// có một kiểu.
import { FRAME_GEOMETRY, type FrameGeometry } from "./frame-geometry";

export type Metal = "gold" | "silver" | "bronze";
export type FrameStyle = "game" | "hoa-hong" | "canh-thien-than" | "ngoc-trai" | "vuong-mien";

/** Thứ tự + tên hiện ở trang Bộ sưu tập. Khớp `cosmetics.Styles` ở máy chủ. */
export const FRAME_STYLES: readonly { key: FrameStyle; label: string }[] = [
  { key: "game", label: "Huyền thoại" },
  { key: "hoa-hong", label: "Đoá hồng" },
  { key: "canh-thien-than", label: "Cánh thiên thần" },
  { key: "ngoc-trai", label: "Ngọc trai" },
  { key: "vuong-mien", label: "Vương miện" },
];

const KIM: Record<Metal, "vang" | "bac" | "dong"> = { gold: "vang", silver: "bac", bronze: "dong" };

const NGOC_THEO_NHOM: Record<string, string> = {
  reign: "giu-ngoi",
  year: "ky-niem-nam",
  month: "ky-niem-thang",
  jade: "ngoc",
  laurel: "nguyet-que",
  star: "sao",
  rise: "but-pha",
  // Hành trình mỗi ngày (QĐ 0468): ngọc hồng — đổi màu từ ngọc giữ ngôi, giữ viền vàng.
  journey: "hanh-trinh",
};

function theoTen(files: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, url] of Object.entries(files)) {
    const ten = path.split("/").pop()?.replace(/\.webp$/, "");
    if (ten) out[ten] = url;
  }
  return out;
}

const TRON = theoTen(
  import.meta.glob<string>("../../assets/khung-hang/tron/*.webp", { eager: true, import: "default", query: "?url" }),
);
const NGOC = theoTen(
  import.meta.glob<string>("../../assets/khung-hang/ngoc/*.webp", { eager: true, import: "default", query: "?url" }),
);
const THE = theoTen(
  import.meta.glob<string>("../../assets/khung-hang/the/*.webp", { eager: true, import: "default", query: "?url" }),
);
const BIEN = theoTen(
  import.meta.glob<string>("../../assets/khung-hang/bien/*.webp", { eager: true, import: "default", query: "?url" }),
);
const KE = theoTen(
  import.meta.glob<string>("../../assets/khung-hang/ke/*.webp", { eager: true, import: "default", query: "?url" }),
);

/** Kim loại của một khung: nhóm theo hạng thì 1 vàng; nhóm theo bậc thì bậc cao là vàng. */
export function metalOf(tier: number, group: string): Metal {
  if (group === "rise") return "gold";
  // Hành trình có 4 bậc (7 · 30 · 100 · 365 ngày): 7 đồng, 30 bạc, 100 và 365 vàng.
  if (group === "journey") return tier <= 1 ? "bronze" : tier === 2 ? "silver" : "gold";
  const byStep = group === "star" || group === "laurel" || group === "jade";
  const t = byStep ? 4 - tier : tier;
  return t <= 1 ? "gold" : t === 2 ? "silver" : "bronze";
}

/** Kiểu lạ (máy chủ mới hơn màn) ⇒ kiểu mặc định, không để trống khung. */
export function styleOf(style: string | null | undefined): FrameStyle {
  return FRAME_STYLES.some((s) => s.key === style) ? (style as FrameStyle) : "game";
}

export type FrameArt = { art: string; gem: string; metal: Metal; geo: FrameGeometry; key: string };

export function frameArtFor(tier: number, group: string, style?: string | null): FrameArt {
  const metal = metalOf(tier, group);
  const key = `${styleOf(style)}-${KIM[metal]}`;
  return {
    art: TRON[key] ?? "",
    gem: NGOC[NGOC_THEO_NHOM[group] ?? "giu-ngoi"] ?? "",
    metal,
    geo: FRAME_GEOMETRY[key] ?? FRAME_GEOMETRY["game-vang"]!,
    key,
  };
}

/** Ngọc của một nhóm (dùng riêng ở khung thẻ, dải hồ sơ). */
export function gemFor(group: string): string {
  return NGOC[NGOC_THEO_NHOM[group] ?? "giu-ngoi"] ?? "";
}

/** Ba lớp khung thẻ chữ nhật của một kim loại: viền (9 ô), mão đỉnh, chóp đáy. */
export function cardArtFor(metal: Metal): { vien: string; mao: string; chop: string } {
  const k = KIM[metal];
  return { vien: THE[k] ?? "", mao: THE[`${k}-mao`] ?? "", chop: THE[`${k}-chop`] ?? "" };
}

/** Biển bậc nghề G1–G5 theo kim loại của khung (không đeo khung ⇒ đồng). */
export function plaqueFor(metal: Metal): string {
  return BIEN[KIM[metal]] ?? "";
}

export const SHELF_BADGES = {
  reign: KE["dang-giu-ngoi"] ?? "",
  year: KE["ky-niem-nam"] ?? "",
  month: KE["ky-niem-thang"] ?? "",
  jade: KE["ngoc-kpi"] ?? "",
  laurel: KE["nguyet-que"] ?? "",
  star: KE["sao-huy-chuong"] ?? "",
  journey: KE["hanh-trinh"] ?? "",
} as const;
